<template>
  <div class="nota-comercial-note-form">
    <div class="mb-3">
      <label class="form-label">{{ $t("notaComercial.noteForm.noteKindLabel") }}</label>
      <select
        :value="value.noteKind"
        class="form-select"
        :disabled="disabled"
        @change="patchField('noteKind', $event.target.value)"
      >
        <option v-for="option in noteKindOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="mb-3">
      <label class="form-label">{{ $t("notaComercial.noteForm.contactRoleLabel") }}</label>
      <select
        v-if="contactRoleIsSelectable"
        :value="effectiveContactRole"
        class="form-select"
        :disabled="disabled"
        @change="patchField('contactRoleOverride', $event.target.value)"
      >
        <option v-for="role in contactRoleOptions" :key="role" :value="role">{{ role }}</option>
      </select>
      <input
        v-else
        :value="effectiveContactRole"
        type="text"
        class="form-control"
        readonly
        :title="$t('notaComercial.noteForm.contactRoleHint')"
      />
      <small class="text-muted">
        {{
          contactRoleIsSelectable
            ? $t("notaComercial.noteForm.contactRoleSupplierHint")
            : $t("notaComercial.noteForm.contactRoleHint")
        }}
      </small>
    </div>

    <div class="mb-3">
      <label class="form-label d-block">{{ $t("Contact") }}</label>
      <EditFieldReference
        :field="contactReferenceField"
        :value="value.contactRef"
        :disabled="disabled || lockContact"
        @input="patchField('contactRef', $event)"
      />
    </div>

    <div class="row g-2">
      <div class="col-6">
        <label class="form-label">{{ $t("Date") }}</label>
        <input
          :value="value.date"
          type="date"
          class="form-control"
          :disabled="disabled"
          @input="patchField('date', $event.target.value)"
        />
      </div>
      <div class="col-6">
        <label class="form-label">{{ $t("Currency") }}</label>
        <select
          :value="value.currencyId"
          class="form-select"
          :disabled="disabled"
          @change="patchField('currencyId', $event.target.value)"
        >
          <option value="">{{ $t("notaComercial.noteForm.noCurrency") }}</option>
          <!-- :selected por opción: las monedas llegan en el mismo tick que el valor y el :value del select no las espera -->
          <option
            v-for="currency in currencyOptions"
            :key="currency._id"
            :value="currency._id"
            :selected="String(currency._id) === String(value.currencyId)"
          >
            {{ currency.label }}
          </option>
        </select>
      </div>
    </div>

    <!--
      Nota nuestra (Giving): el número lo reserva el guardado, así que acá no se muestra.
      Tocarlo antes de guardar solo permitía saltear o repetir un número de la secuencia.
    -->
    <div v-if="showOurNumberField" class="mt-3 small text-muted">
      <i class="fas fa-hashtag me-1" aria-hidden="true"></i>
      {{ numberGeneratedOnSaveHint }}
    </div>

    <div v-else class="mt-3">
      <label class="form-label">{{ $t("notaComercial.noteForm.numberLabelReceived") }}</label>
      <input
        :value="value.number"
        type="text"
        class="form-control"
        :placeholder="$t('notaComercial.noteForm.numberPlaceholderReceived')"
        :disabled="disabled"
        @input="patchField('number', $event.target.value)"
      />
      <div class="form-text">{{ $t("notaComercial.noteForm.numberHintReceived") }}</div>
    </div>

    <div class="mt-3">
      <label class="form-label">{{ $t("Amount") }}</label>
      <input
        :value="value.amount"
        min="0"
        step="0.01"
        type="number"
        class="form-control"
        :disabled="disabled"
        @input="onAmountInput($event.target.value)"
      />
    </div>

    <div v-if="!lockAssignment" class="mt-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <label class="form-label mb-0">{{ $t("notaComercial.noteForm.assignmentLabel") }}</label>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          :disabled="disabled || remainingAmount <= 1e-6"
          @click="addAppliedToLine"
        >
          <i class="fas fa-plus me-1" aria-hidden="true"></i>
          {{ $t("Add") }}
        </button>
      </div>

      <div v-if="!appliedToLines.length" class="small text-muted mb-2">
        {{ $t("notaComercial.noteForm.assignmentNone") }}
      </div>

      <div
        v-for="(line, index) in appliedToLines"
        :key="line.key"
        class="border rounded p-2 mb-2"
      >
        <div class="row g-2 align-items-end">
          <div class="col-12 col-md-3">
            <label class="form-label">{{ $t("notaComercial.noteForm.assignmentTarget") }}</label>
            <select
              :value="line.targetType"
              class="form-select"
              :disabled="disabled || isLineTargetLocked(index)"
              @change="patchAppliedToLine(index, 'targetType', $event.target.value)"
            >
              <option value="shipping">{{ $t("Shipping") }}</option>
              <option value="proform">{{ $t("Proform") }}</option>
            </select>
          </div>

          <div class="col-12 col-md-5">
            <label class="form-label d-block">
              {{ line.targetType === "proform" ? $t("Proform") : $t("Shipping") }}
            </label>
            <EditFieldReference
              v-if="line.targetType === 'shipping'"
              :field="shippingReferenceField"
              :value="line.shippingRef"
              :disabled="disabled || isLineTargetLocked(index)"
              @input="patchAppliedToLine(index, 'shippingRef', $event)"
            />
            <EditFieldReference
              v-else
              :field="proformReferenceField"
              :value="line.proformRef"
              :disabled="disabled || isLineTargetLocked(index)"
              @input="patchAppliedToLine(index, 'proformRef', $event)"
            />
          </div>

          <div class="col-8 col-md-3">
            <label class="form-label">{{ $t("Amount") }}</label>
            <input
              :value="line.amount"
              min="0"
              step="0.01"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': appliedExceeds }"
              :disabled="disabled"
              :max="maxAmountForLine(index)"
              @input="onLineAmountInput(index, $event.target.value)"
            />
          </div>

          <div class="col-4 col-md-1 d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-outline-danger"
              :disabled="disabled || isLineTargetLocked(index)"
              :title="$t('Remove')"
              :aria-label="$t('Remove')"
              @click="removeAppliedToLine(index)"
            >
              <i class="fas fa-trash" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="appliedToLines.length"
        class="small d-flex justify-content-between flex-wrap gap-2"
        :class="appliedExceeds ? 'text-danger fw-semibold' : 'text-muted'"
      >
        <span>{{ $t("Applied") }}: {{ formatMoney(appliedSum) }}</span>
        <span>{{ $t("Remaining") }}: {{ formatMoney(remainingAmount) }}</span>
      </div>
      <div v-if="appliedExceeds" class="alert alert-danger py-2 mt-2 mb-0">
        <i class="fas fa-exclamation-triangle me-1" aria-hidden="true"></i>
        {{ $t("notaComercial.noteForm.validationAppliedExceeds") }}
      </div>
    </div>

    <div v-if="lockAssignment && assignmentHint" class="mt-3 small text-muted">
      {{ assignmentHint }}
    </div>

    <div class="mt-3">
      <label class="form-label">{{ $t("Description") }}</label>
      <textarea
        :value="value.description"
        class="form-control"
        rows="3"
        :placeholder="$t('notaComercial.noteForm.descriptionPlaceholder')"
        :disabled="disabled"
        @input="patchField('description', $event.target.value)"
      />
    </div>

    <div class="mt-3">
      <label class="form-label">{{ $t("Comment") }}</label>
      <textarea
        :value="value.comment"
        class="form-control"
        rows="3"
        :placeholder="$t('notaComercial.noteForm.commentPlaceholder')"
        :disabled="disabled"
        @input="patchField('comment', $event.target.value)"
      />
    </div>

    <div class="mt-3">
      <label class="form-label d-block">{{ $t("notaComercial.noteForm.attachFileLabel") }}</label>
      <EditFieldFile
        :field="fileField"
        :value="value.file"
        disable-lock
        @input="patchField('file', normalizeFileValue($event))"
      />
      <div class="form-text">{{ $t("notaComercial.noteForm.attachFileHint") }}</div>
    </div>
  </div>
</template>

<script>
import EditFieldReference from "@/components/gotrading/model-definition-core/reference/editField.vue";
import EditFieldFile from "@/components/gotrading/model-definition-core/file/editField.vue";
import {
  appliedToExceedsAmount,
  clampAllAppliedToLinesToAmount,
  clampAppliedToLineAmount,
  createEmptyAppliedToLine,
  getNoteKindOptions,
  maxAmountForAppliedToLine,
  remainingAppliedToAmount,
  sumAppliedToLines,
  syncSingleAppliedToLineAmount,
  contactRoleForForm,
  noteKindContactRole,
  noteKindUsesOurNumbering,
  noteKindIsCredit,
  applyNoteKindContact,
} from "../utils/notaComercialNoteFormUtils";

export default {
  name: "NotaComercialNoteForm",
  components: {
    EditFieldReference,
    EditFieldFile,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    lists: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    lockContact: {
      type: Boolean,
      default: false,
    },
    lockAssignment: {
      type: Boolean,
      default: false,
    },
    lockShipping: {
      type: Boolean,
      default: false,
    },
    lockProform: {
      type: Boolean,
      default: false,
    },
    assignmentHint: {
      type: String,
      default: "",
    },
    /**
     * Contactos de la operación: el formulario elige el que corresponde a la variante
     * (proveedor en las notas del proveedor, cliente en las nuestras).
     */
    contacts: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    contactReferenceField() {
      return {
        key: "contact",
        label: "Contacto",
        type: "reference",
        ref: "Contact",
        isArray: false,
        preload: false,
      };
    },
    shippingReferenceField() {
      return {
        key: "shipping",
        label: "Shipping",
        type: "reference",
        ref: "shipping",
        isArray: false,
        preload: false,
      };
    },
    proformReferenceField() {
      return {
        key: "proform",
        label: "Proforma",
        type: "reference",
        ref: "Proform",
        isArray: false,
        preload: false,
      };
    },
    /** Rol con el que se va a guardar: el de la variante, o el tipo de la línea de gasto. */
    effectiveContactRole() {
      return contactRoleForForm(this.value);
    },
    /**
     * El rol solo se elige del lado del proveedor. Del lado del cliente define el lado
     * contable de la nota: cambiarlo la daría vuelta en silencio.
     */
    contactRoleIsSelectable() {
      return noteKindContactRole(this.value && this.value.noteKind) === "Supplier";
    },
    /**
     * «Supplier» más los tipos de gasto del tenant, que es lo que guarda una nota nacida
     * desde una línea de gasto. El separador del textlist es "|": una etiqueta que lo
     * lleve no puede ser opción.
     */
    contactRoleOptions() {
      const list = (this.lists || []).find((l) => l && String(l.type || "") === "typeofpayment");
      const labels = ((list && list.values) || [])
        .map((v) => (v && v.label ? String(v.label).trim() : ""))
        .filter((label) => label && !label.includes("|"));
      const unique = [...new Set(["Supplier", ...labels])];
      const current = this.effectiveContactRole;
      if (current && !unique.includes(current)) unique.push(current);
      return unique;
    },
    noteKindOptions() {
      return getNoteKindOptions((key) => this.$t(key));
    },
    currencyOptions() {
      const list = this.lists.find((l) => l && l.type === "currency");
      if (!list || !Array.isArray(list.values)) return [];
      return list.values
        .filter((v) => v && v._id)
        .map((v) => ({ _id: v._id, label: v.label || String(v._id) }));
    },
    appliedToLines() {
      return Array.isArray(this.value.appliedToLines) ? this.value.appliedToLines : [];
    },
    appliedSum() {
      return sumAppliedToLines(this.appliedToLines);
    },
    remainingAmount() {
      return remainingAppliedToAmount(this.value);
    },
    appliedExceeds() {
      return appliedToExceedsAmount(this.value);
    },
    showOurNumberField() {
      return noteKindUsesOurNumbering(this.value.noteKind);
    },
    numberGeneratedOnSaveHint() {
      return noteKindIsCredit(this.value.noteKind)
        ? this.$t("notaComercial.noteForm.numberGeneratedOnSaveCredit")
        : this.$t("notaComercial.noteForm.numberGeneratedOnSaveDebit");
    },
    fileField() {
      return {
        key: "file",
        label: "File",
        type: "file",
        isArray: true,
        required: false,
        readonly: this.disabled,
      };
    },
  },
  methods: {
    /**
     * El adjunto es múltiple: el control emite un array, pero ante un solo archivo
     * viejo o al borrarlos todos puede llegar un string o null. El formulario guarda
     * siempre un array para no mezclar las dos formas en el payload.
     */
    normalizeFileValue(value) {
      if (Array.isArray(value)) return value.filter(Boolean);
      if (typeof value === "string" && value.trim()) return [value.trim()];
      return [];
    },

    formatMoney(value) {
      const num = Number(value) || 0;
      return new Intl.NumberFormat("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    },
    maxAmountForLine(index) {
      return maxAmountForAppliedToLine(this.value, index);
    },
    patchField(key, nextValue) {
      let next = {
        ...this.value,
        [key]: nextValue,
      };
      if (key === "noteKind") {
        // La variante manda sobre el contacto: proveedor del embarque o cliente.
        next = applyNoteKindContact(next, this.contacts);
        // El número nuestro lo genera el guardado; el manual es solo del emisor.
        if (noteKindUsesOurNumbering(nextValue)) next.number = "";
      }
      this.$emit("input", next);
    },
    onAmountInput(raw) {
      const amount = Number(raw);
      let next = {
        ...this.value,
        amount: Number.isFinite(amount) ? amount : 0,
      };
      next = clampAllAppliedToLinesToAmount(next);
      this.$emit("input", next);
    },
    isLineTargetLocked(index) {
      if (!this.lockAssignment) return false;
      if (index !== 0) return false;
      return !!(this.lockShipping || this.lockProform);
    },
    emitAppliedToLines(nextLines) {
      let next = {
        ...this.value,
        appliedToLines: nextLines,
      };
      next = syncSingleAppliedToLineAmount(next);
      this.$emit("input", next);
    },
    addAppliedToLine() {
      const remaining = remainingAppliedToAmount(this.value);
      const nextLines = [
        ...this.appliedToLines,
        createEmptyAppliedToLine({
          targetType: "shipping",
          amount: remaining > 0 ? remaining : 0,
        }),
      ];
      this.emitAppliedToLines(nextLines);
    },
    removeAppliedToLine(index) {
      const nextLines = this.appliedToLines.filter((_, i) => i !== index);
      this.emitAppliedToLines(nextLines);
    },
    onLineAmountInput(index, raw) {
      const clamped = clampAppliedToLineAmount(this.value, index, raw);
      this.patchAppliedToLine(index, "amount", clamped);
    },
    patchAppliedToLine(index, key, nextValue) {
      const nextLines = this.appliedToLines.map((line, i) => {
        if (i !== index) return line;
        const patched = { ...line, [key]: nextValue };
        if (key === "targetType") {
          if (nextValue === "shipping") patched.proformRef = null;
          if (nextValue === "proform") patched.shippingRef = null;
        }
        return patched;
      });
      this.$emit("input", {
        ...this.value,
        appliedToLines: nextLines,
      });
    },
  },
};
</script>
